import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Line } from 'react-chartjs-2';
import { useUser } from 'context/user/User';
import statisticsApi from 'api/requests/statistics';
import currency from 'helpers/currency';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';

const PERIOD_OPTIONS = [
    { label: 'Hoje',   value: 'day' },
    { label: 'Semana', value: 'week' },
    { label: 'Mês',    value: 'month' },
];

const PAYMENT_COLORS = [
    colors.occasionalPurple,
    colors.primary,
    colors.occasionalGreen,
    colors.occasionalOrange,
    colors.occasionalPink,
];

const HOURS_LABELS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}h`);

export default function RestaurantStatisticsPage() {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;

    const [period, setPeriod] = useState('day');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [summary, setSummary] = useState(null);
    const [hourly, setHourly] = useState([]);
    const [topProducts, setTopProducts] = useState([]);

    useEffect(() => {
        if (!slug) return;
        if (period === 'custom') {
            if (customStart && customEnd) fetchAll();
        } else {
            fetchAll();
        }
    }, [slug, period, customStart, customEnd]);

    function handleSelectPeriod(value) {
        setPeriod(value);
        if (value !== 'custom') {
            setCustomStart('');
            setCustomEnd('');
        }
    }

    function formatDateLabel(dateStr) {
        if (!dateStr) return '';
        const [, month, day] = dateStr.split('-');
        return `${day}/${month}`;
    }

    async function fetchAll() {
        setIsLoading(true);
        setError(null);
        try {
            const params = period === 'custom'
                ? { start_date: customStart, end_date: customEnd }
                : { period };
            const [summaryRes, hourlyRes, productsRes] = await Promise.all([
                statisticsApi.summary(slug, params),
                statisticsApi.revenueByHour(slug, params),
                statisticsApi.topProducts(slug, params),
            ]);
            setSummary(summaryRes.data);
            setHourly(hourlyRes.data.data || []);
            setTopProducts(productsRes.data.data || []);
        } catch {
            setError('Erro ao carregar estatísticas.');
        } finally {
            setIsLoading(false);
        }
    }

    const topPayment = summary?.payment_breakdown?.reduce(
        (best, m) => (!best || m.percentage > best.percentage ? m : best),
        null
    );

    const hourlyChartData = {
        labels: HOURS_LABELS,
        datasets: [{
            label: 'Receita',
            data: hourly.map(d => d.total),
            borderColor: colors.occasionalPurple,
            backgroundColor: 'rgba(159, 22, 219, 0.08)',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 3,
            pointBackgroundColor: colors.occasionalPurple,
            pointHoverRadius: 5,
        }],
    };

    const hourlyChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#999',
                    fontSize: 11,
                    callback: v => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : v,
                },
                gridLines: { color: '#f0f0f0', drawBorder: false },
            }],
            xAxes: [{
                ticks: {
                    fontColor: '#999',
                    fontSize: 10,
                    maxRotation: 0,
                    autoSkip: true,
                    maxTicksLimit: 12,
                },
                gridLines: { display: false },
            }],
        },
        legend: { display: false },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            xPadding: 12,
            yPadding: 10,
            callbacks: {
                label: (item) => ` ${currency.format(item.yLabel)}`,
            },
        },
    };

    const maxProductQty = topProducts[0]?.quantity_sold || 1;

    return (
        <Page>
            <PeriodBar>
                {PERIOD_OPTIONS.map(opt => (
                    <PeriodPill
                        key={opt.value}
                        $active={period === opt.value}
                        onClick={() => handleSelectPeriod(opt.value)}
                    >
                        {opt.label}
                    </PeriodPill>
                ))}
                <PeriodPill
                    $active={period === 'custom'}
                    onClick={() => handleSelectPeriod('custom')}
                >
                    {period === 'custom' && customStart && customEnd
                        ? `${formatDateLabel(customStart)} – ${formatDateLabel(customEnd)}`
                        : 'Personalizado'}
                </PeriodPill>
            </PeriodBar>

            {period === 'custom' && (
                <DateRangeRow>
                    <DateInput
                        type="date"
                        value={customStart}
                        max={customEnd || undefined}
                        onChange={e => setCustomStart(e.target.value)}
                    />
                    <DateRangeSep>–</DateRangeSep>
                    <DateInput
                        type="date"
                        value={customEnd}
                        min={customStart || undefined}
                        onChange={e => setCustomEnd(e.target.value)}
                    />
                </DateRangeRow>
            )}

            {isLoading && (
                <LoadingWrap>
                    <Spinner />
                </LoadingWrap>
            )}

            {!isLoading && error && (
                <ErrorWrap>
                    <ErrorText>{error}</ErrorText>
                    <RetryBtn onClick={fetchAll}>Tentar novamente</RetryBtn>
                </ErrorWrap>
            )}

            {!isLoading && !error && summary && (
                <>
                    <KpiGrid>
                        <KpiCard $accent={colors.occasionalPurple}>
                            <KpiLabel>Receita Total</KpiLabel>
                            <KpiValue>{currency.format(summary.total_revenue)}</KpiValue>
                        </KpiCard>
                        <KpiCard $accent={colors.primary}>
                            <KpiLabel>Comandas Fechadas</KpiLabel>
                            <KpiValue>{summary.commands_count}</KpiValue>
                            <KpiSub>comandas</KpiSub>
                        </KpiCard>
                        <KpiCard $accent={colors.occasionalGreen}>
                            <KpiLabel>Ticket Médio</KpiLabel>
                            <KpiValue>{currency.format(summary.average_command_value)}</KpiValue>
                            <KpiSub>por comanda</KpiSub>
                        </KpiCard>
                        <KpiCard $accent={colors.occasionalOrange}>
                            <KpiLabel>Método Principal</KpiLabel>
                            <KpiValue>{topPayment?.payment_method || '—'}</KpiValue>
                            {topPayment && <KpiSub>{topPayment.percentage}% das vendas</KpiSub>}
                        </KpiCard>
                    </KpiGrid>

                    <ChartsRow>
                        <ChartCard>
                            <CardHeader>
                                <CardTitle>Receita por Hora</CardTitle>
                                <CardSub>Acumulado por hora do dia no período</CardSub>
                            </CardHeader>
                            <ChartWrap>
                                <ChartInner>
                                    <Line data={hourlyChartData} options={hourlyChartOptions} />
                                </ChartInner>
                            </ChartWrap>
                        </ChartCard>

                        <PaymentCard>
                            <CardHeader>
                                <CardTitle>Formas de Pagamento</CardTitle>
                                <CardSub>{summary.payment_breakdown?.length || 0} métodos utilizados</CardSub>
                            </CardHeader>
                            {summary.payment_breakdown?.length === 0 && (
                                <EmptyText>Sem dados para o período</EmptyText>
                            )}
                            <PaymentList>
                                {(summary.payment_breakdown || [])
                                    .slice()
                                    .sort((a, b) => b.percentage - a.percentage)
                                    .map((item, i) => (
                                        <PaymentRow key={item.payment_method}>
                                            <PaymentTop>
                                                <PaymentDot $color={PAYMENT_COLORS[i % PAYMENT_COLORS.length]} />
                                                <PaymentName>{item.payment_method}</PaymentName>
                                                <PaymentPct>{item.percentage}%</PaymentPct>
                                                <PaymentAmt>{currency.format(item.total_amount)}</PaymentAmt>
                                            </PaymentTop>
                                            <PaymentBarTrack>
                                                <PaymentBarFill
                                                    $pct={item.percentage}
                                                    $color={PAYMENT_COLORS[i % PAYMENT_COLORS.length]}
                                                />
                                            </PaymentBarTrack>
                                        </PaymentRow>
                                    ))}
                            </PaymentList>
                        </PaymentCard>
                    </ChartsRow>

                    <ProductsCard>
                        <CardHeader>
                            <CardTitle>Produtos Mais Vendidos</CardTitle>
                            <CardSub>Top {topProducts.length} produtos no período</CardSub>
                        </CardHeader>
                        {topProducts.length === 0 && (
                            <EmptyText>Sem dados para o período</EmptyText>
                        )}
                        <ProductList>
                            {topProducts.map((product, i) => (
                                <ProductRow key={product.product_id}>
                                    <ProductRank $top={i === 0}>{i + 1}</ProductRank>
                                    <ProductInfo>
                                        <ProductName>{product.name}</ProductName>
                                        <ProductBarTrack>
                                            <ProductBarFill
                                                $pct={(product.quantity_sold / maxProductQty) * 100}
                                            />
                                        </ProductBarTrack>
                                    </ProductInfo>
                                    <ProductStats>
                                        <ProductQty>{product.quantity_sold} vendas</ProductQty>
                                        <ProductRevenue>{currency.format(product.total_revenue)}</ProductRevenue>
                                    </ProductStats>
                                </ProductRow>
                            ))}
                        </ProductList>
                    </ProductsCard>
                </>
            )}
        </Page>
    );
}

/* ─── Layout ─── */

const Page = styled.div`
    padding: 24px 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const LoadingWrap = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 80px 0;
`;

const ErrorWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 60px 0;
`;

const ErrorText = styled.p`
    color: ${colors.danger};
    font-size: 14px;
    margin: 0;
`;

const RetryBtn = styled.button`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.primary};
    background: none;
    border: 1.5px solid ${colors.primary};
    border-radius: 8px;
    padding: 8px 20px;
    cursor: pointer;
`;

/* ─── Period filter ─── */

const PeriodBar = styled.div`
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
`;

const DateRangeRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const DateInput = styled.input`
    height: 36px;
    padding: 0 12px;
    border: 1.5px solid ${colors.occasionalPurple};
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.nightDark};
    background: #fff;
    outline: none;
    cursor: pointer;
    flex: 1;
    min-width: 0;
    &::-webkit-calendar-picker-indicator { cursor: pointer; opacity: 0.6; }
`;

const DateRangeSep = styled.span`
    font-size: 14px;
    color: ${colors.nightLight};
    flex-shrink: 0;
`;

const PeriodPill = styled.button`
    padding: 7px 18px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s;
    border: 1.5px solid ${({ $active }) => $active ? colors.occasionalPurple : '#e0e0e0'};
    background: ${({ $active }) => $active ? colors.occasionalPurple : '#fff'};
    color: ${({ $active }) => $active ? '#fff' : colors.night};

    &:hover {
        border-color: ${colors.occasionalPurple};
        color: ${({ $active }) => $active ? '#fff' : colors.occasionalPurple};
    }
`;

/* ─── KPI cards ─── */

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: ${breakpoints.md}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const KpiCard = styled.div`
    background: #fff;
    border-radius: 14px;
    padding: 20px;
    border-left: 4px solid ${({ $accent }) => $accent};
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const KpiLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${colors.nightLight};
`;

const KpiValue = styled.span`
    font-size: 22px;
    font-weight: 800;
    color: ${colors.nightDark};
    line-height: 1.1;
`;

const KpiSub = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
`;

/* ─── Charts row ─── */

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 16px;

    @media (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const BaseCard = styled.div`
    background: #fff;
    border-radius: 14px;
    padding: 20px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.06);
    min-width: 0;
    overflow: hidden;
`;

const ChartCard = styled(BaseCard)``;

const PaymentCard = styled(BaseCard)`
    display: flex;
    flex-direction: column;
`;

const ProductsCard = styled(BaseCard)``;

const CardHeader = styled.div`
    margin-bottom: 16px;
`;

const CardTitle = styled.h3`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 2px;
`;

const CardSub = styled.p`
    font-size: 11px;
    color: ${colors.nightLight};
    margin: 0;
`;

const ChartWrap = styled.div`
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
`;

const ChartInner = styled.div`
    height: 260px;
    min-width: 420px;
    position: relative;
`;

const EmptyText = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    text-align: center;
    padding: 20px 0;
    margin: 0;
`;

/* ─── Payment breakdown ─── */

const PaymentList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex: 1;
    justify-content: center;
`;

const PaymentRow = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const PaymentTop = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
`;

const PaymentDot = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
`;

const PaymentName = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightDark};
    flex: 1;
`;

const PaymentPct = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${colors.night};
`;

const PaymentAmt = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
    text-align: right;
    white-space: nowrap;
    flex-shrink: 0;
`;

const PaymentBarTrack = styled.div`
    height: 5px;
    background: #f0f0f0;
    border-radius: 3px;
    overflow: hidden;
`;

const PaymentBarFill = styled.div`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${({ $color }) => $color};
    border-radius: 3px;
    transition: width 0.4s ease;
`;

/* ─── Top products ─── */

const ProductList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;

const ProductRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
`;

const ProductRank = styled.span`
    font-size: 13px;
    font-weight: 800;
    color: ${({ $top }) => $top ? colors.occasionalPurple : colors.nightLight};
    min-width: 20px;
    text-align: center;
`;

const ProductInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
`;

const ProductName = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const ProductBarTrack = styled.div`
    height: 4px;
    background: #f0f0f0;
    border-radius: 2px;
    overflow: hidden;
`;

const ProductBarFill = styled.div`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${colors.occasionalPurple};
    border-radius: 2px;
    opacity: 0.5;
    transition: width 0.4s ease;
`;

const ProductStats = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    flex-shrink: 0;
`;

const ProductQty = styled.span`
    font-size: 12px;
    font-weight: 700;
    color: ${colors.nightDark};
`;

const ProductRevenue = styled.span`
    font-size: 11px;
    color: ${colors.nightLight};
`;

/* ─── Spinner ─── */

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const Spinner = styled.div`
    width: 32px;
    height: 32px;
    border: 3px solid #eee;
    border-top-color: ${colors.occasionalPurple};
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;
